<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->
<template>
  <div class="billRateOthers-main">
    <Menus :menu="menus" @menuClicked="handleMenuClick" />

    <PopupEntry
      v-if="showPopup"
      :show.sync="showPopup"
      :title="titleBillRateOthers"
      @save="saveBillRateOthers"
      :requiredError="entryError"
      :errorMessages="errorMessage"
      :viewMode="viewMode"
      @requiredClicked="errorMessageClicked"
      :successMessages="successMessages"
      :createdBy="createdBy"
      :updatedBy="updatedBy"
    >
      <InputQuery
        placeholder="Charges Code"
        :isDisabled="disableChargeCode"
        :items="chargeCodeList"
        itemKey="combinedChargeCodeName"
        :selectedObject.sync="chargeCode"
        descKey="Description"
        :isRequired="true"
        ref="chargeCode"
        validator="alphanumeric,10,uppercase"
        :errorMessage.sync="errorChargeCode"
        @focusChanged="onInputFocusChanged"
      />

      <InputQuery
        placeholder="Billing Customer Code"
        :isDisabled="disableBillingCustomerCode"
        :items="customerCodeList"
        :selectedObject.sync="billingCustomerCode"
        itemKey="combinedCustomerCode"
        descKey="Customer Name"
        validator="alphanumeric,10,uppercase"
        @focusChanged="onInputFocusChanged"
        ref="billingCustomerCode"
      />

      <Dropdown
        placeholder="Job Type"
        :isDisabled="disableJobType"
        :items="jobTypeList"
        itemKey="Description"
        :selectedObject.sync="selectedJobType"
        validator="alphanumeric,10,uppercase"
      />

      <Dropdown
        placeholder="Rate Category"
        :isDisabled="disableRateCategory"
        :items="rateCategoryList"
        :isRequired="true"
        itemKey="BillRateGroup"
        :selectedObject.sync="selectedRateCategory"
        validator="text,30,,alphanumeric,all"
        ref="rateCategory"
        :errorMessage.sync="errorRateCategory"
      />

      <Dropdown
        v-if="showReferenceInput"
        :placeholder="tableHCharges"
        :isDisabled="disableReferenceValue"
        :items="referenceList"
        :selectedObject.sync="selectedReferenceValue"
        validator="alphanumeric,50,uppercase"
        itemKey="referenceValue"
        ref="Reference Value"
        :isRequired="isReferenceValueRequired"
        :errorMessage.sync="errorReference"
      />

      <RadioButtonSet
        title="Set rates for..."
        :buttons="setRateList"
        labelKey="label"
        :selected.sync="selectedSetRate"
        :isDisabled="disableSetRate"
        :isHorizontal="true"
        @radioChanged="onRateTypeChanged"
      />

      <div v-if="selectedSetRateKeyValue === 0">
        <!-- Zone-to-Zone -->
        <InputQuery
          placeholder="From Zone"
          :isDisabled="disableFrom"
          :items="zoneList"
          itemKey="combinedZone"
          descKey="Description"
          :selectedObject.sync="selectedFrom"
          validator="alphanumeric,10,uppercase"
          style="margin-top: 6px"
        />

        <InputQuery
          placeholder="To Zone"
          :isDisabled="disableTo"
          :items="zoneList"
          itemKey="combinedZone"
          :selectedObject.sync="selectedTo"
          validator="alphanumeric,10,uppercase"
          descKey="Description"
        />
      </div>

      <div v-else-if="selectedSetRateKeyValue === 1">
        <!-- Area-to-Area -->
        <InputQuery
          placeholder="From Area"
          :isDisabled="disableFrom"
          :items="areaList"
          itemKey="combinedArea"
          descKey="Description"
          validator="alphanumeric,10,uppercase"
          :selectedObject.sync="selectedFrom"
          style="margin-top: 6px"
        />

        <InputQuery
          placeholder="To Area"
          :isDisabled="disableTo"
          :items="areaList"
          itemKey="combinedArea"
          :selectedObject.sync="selectedTo"
          validator="alphanumeric,10,uppercase"
          descKey="Description"
        />
      </div>

      <div v-else-if="selectedSetRateKeyValue === 2">
        <!-- Destination-to-Destination -->
        <InputQuery
          placeholder="From Destination"
          :isDisabled="disableFrom"
          :items="destinationList"
          itemKey="combinedDestinationCode"
          :selectedObject.sync="selectedFrom"
          validator="alphanumeric,10,uppercase"
          descKey="Company"
          style="margin-top: 6px"
        />

        <InputQuery
          placeholder="To Destination"
          :isDisabled="disableTo"
          :items="destinationList"
          itemKey="combinedDestinationCode"
          :selectedObject.sync="selectedTo"
          validator="alphanumeric,10,uppercase"
          descKey="Company"
        />
      </div>

      <InputQuery
        placeholder="Currency"
        :items="currencyList"
        itemKey="combinedCurrency"
        :isDisabled="disableCurrency"
        descKey="Description"
        :selectedObject.sync="currency"
        :isRequired="true"
        ref="currency"
        :errorMessage.sync="errorCurrencyCode"
      />

      <div class="flex-row">
        <Input
          placeholder="Rate"
          :inputValue.sync="rate"
          :isDisabled="disableRate"
          :isRequired="true"
          style="padding-right: 10px"
          ref="rate"
          :errorMessage.sync="errorRate"
          validator="positiveDecimal,14,4"
        />
        <Dropdown
          placeholder="Rate Type"
          :items="rateTypeList"
          :isDisabled="disableRateType"
          itemKey="label"
          :selectedObject.sync="selectedRateType"
          ref="rateType"
          :errorMessage.sync="errorRateType"
        />
      </div>

      <div class="flex-row">
        <Input
          placeholder="Mark-Up Rate"
          :inputValue.sync="markRate"
          :isDisabled="disableMarkUpRate"
          :isRequired="true"
          style="padding-right: 10px"
          ref="markRate"
          :errorMessage.sync="errorMarkRate"
          validator="positiveDecimal,14,4"
        />
        <Dropdown
          placeholder="MarkUp Rate Type"
          :items="rateTypeList"
          :isDisabled="disableMarkUpRateType"
          itemKey="label"
          :selectedObject.sync="selectedMarkUpRate"
          ref="markUpRateType"
          :errorMessage.sync="errorMarkUpRateType"
        />
      </div>

      <div class="flex-row">
        <Input
          placeholder="Per Journey Rate"
          :inputValue.sync="journeyRate"
          :isDisabled="disableJourneyRate"
          validator="positiveDecimal,14,4"
          style="padding-right: 10px"
        />
        <InputQuery
          placeholder="Truck Type"
          :isDisabled="disableDescription"
          :items="truckTypeList"
          :selectedObject.sync="truckType"
          descKey="Truck Description"
          itemKey="combinedTruckType"
        />
      </div>

      <div class="flex-row">
        <Input
          placeholder="Droppoint Rate"
          :inputValue.sync="droppointRate"
          :isDisabled="disableDroppointRate"
          validator="positiveDecimal,14,4"
          style="padding-right: 10px"
        />
        <Input
          placeholder="if # of drops >"
          :inputValue.sync="drop"
          :isDisabled="disableDrop"
          validator="positiveInteger,5"
        />
      </div>
      <DateTimePicker
        placeholder="Valid From"
        :value.sync="validFrom"
        :showTime="false"
        :isDisabled="disableDate"
        :isRequired="true"
        :maxDate="new Date('2079-06-06')"
        ref="Date"
        :errorMessage.sync="errorDate"
      />
    </PopupEntry>

    <div class="right-container">
      <TableBox
        title="Bill Rate Others"
        :tableData="billList"
        :maxSelection="-1"
        :selectedRow.sync="selectedBillRateOthers"
        :dataType="{
          Rate: 'currency',
          'MarkUp Rate': 'currency',
          'Per Journey Rate': 'currency',
          'Drop point Rate': 'currency',
        }"
      />
    </div>
  </div>
</template>

<script>
export default {
  data: function () {
    return {
      menus: [
        {
          title: 'New Bill Rate Others',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          key: 'MnuNew',
          visible: true,
          enabled: false,
        },
        {
          title: 'Edit Bill Rate Others',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-edit-64%20(1).png',
          key: 'MnuEdit',
          visible: true,
          enabled: false,
        },
        {
          title: 'Delete Bill Rate Others',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png',
          key: 'MnuDelete',
          visible: false,
          enabled: false,
        },
        {
          title: 'View Bill Rate Others',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png',
          key: 'MnuQuery',
          visible: true,
          enabled: false,
        },
        {
          title: 'Duplicate',
          icon: 'https://dsmlib.logistics.systems/Icons/DuplicateJob.png',
          enabled: false,
        },
      ],
      errorMessage: [],
      successMessages: [],
      //data from API GET
      chargeCode: null,
      billingCustomerCode: null,
      createdBy: '',
      updatedBy: '',
      setRateList: [
        { label: 'Zone-to-Zone', keyValue: 0 },
        { label: 'Area-to-Area', keyValue: 1 },
        { label: 'Destination-to-Destination', keyValue: 2 },
      ],
      rateTypeList: [
        { label: '$', keyValue: '$' },
        { label: '%', keyValue: '%' },
      ],
      jobTypeList: [],
      rateCategoryList: [],
      chargeCodeList: [],
      showReferenceInput: false,
      referenceList: [],
      viewMode: false,
      billList: [],
      selectedBillRateOthers: [],
      fieldHCharges: '',
      tableHCharges: '',
      showPopup: false,
      selectedJobType: null,
      selectedRateCategory: null,
      selectedSetRate: null,
      disableRateCategory: false,
      isReferenceValueRequired: false,
      disableJobType: false,
      disableSetRate: false,
      disableDate: false,
      errorChargeCode: '',
      errorRateCategory: '',
      errorReference: '',
      errorRate: '',
      errorMarkRate: '',
      errorJourneyRate: '',
      errorDroppointRate: '',
      errorDrop: '',
      errorMarkUpRateType: '',
      errorCurrencyCode: '',
      errorRateType: '',
      errorDate: '',
      selectedReferenceValue: null,
      currency: null,
      seqno: '',
      disableReferenceValue: false,
      disableBillingCustomerCode: false,
      disableDescription: false,
      disableChargeCode: false,
      disableMarkUpRate: false,
      disableJourneyRate: false,
      disableDroppointRate: false,
      disableDrop: false,
      disableRate: false,
      disableRateType: false,
      disableMarkUpRateType: false,
      disableCurrency: false,

      validFrom: null,
      rate: '',
      markRate: '',
      journeyRate: '',
      droppointRate: '',
      drop: '',
      currencyList: [],
      zoneList: [],
      selectedFrom: null,
      selectedTo: null,
      disableFrom: false,
      disableTo: false,
    };
  },
  computed: {
    selectedSetRateKeyValue() {
      return this.selectedSetRate ? this.selectedSetRate.keyValue : -1;
    },
  },
  methods: {
    errorMessageClicked: function (item) {
      this.$nextTick(() => {
        this.$refs[item].setFocus();
      });
    },

    clearErrorMessages: function () {
      this.errorRateCategory = '';
      this.errorChargeCode = '';
      this.errorRate = '';
      this.errorMarkRate = '';
      this.errorJourneyRate = '';
      this.errorDroppointRate = '';
      this.errorDrop = '';
      this.errorDate = '';
      this.errorCurrencyCode = '';
      this.errorMarkUpRateType = '';
      this.errorRateType = '';
      this.errorReference = '';
    },

    onInputFocusChanged: function () {
      if (!this.viewMode) {
        this.getRefList();
      }
    },
    clearInputFields: function () {
      this.chargeCode = null;
      this.referenceList = [];
      this.selectedReferenceValue = null;
      this.showReferenceInput = false;
      this.billingCustomerCode = null;
      this.selectedJobType = null;
      this.selectedRateCategory = null;
      this.selectedSetRate = null;
      this.selectedFrom = null;
      this.selectedTo = null;
      this.currency = null;
      this.rate = '';
      this.selectedRateType = null;
      this.markRate = '';
      this.selectedMarkUpRate = null;
      this.journeyRate = '';
      this.truckType = null;
      this.droppointRate = '';
      this.drop = '';
      this.updatedBy = '';
      this.createdBy = '';
    },

    getSpecificBillRateOthers: async function (record) {
      this.$fullscreenLoading('Getting Details');
      const details = (
        await this.$apiCallV2(`/Master/GetDetailsMaster`, {
          masterfile: 'BILLRATEOTHERS',
          recordid: record['Seq'],
          chargescode: record['Charges Code'],
        })
      )['BillRatesOther'];

      this.seqno = details['Seq No'] || '';
      this.chargeCode = this.chargeCodeList.getSelected(details['Charges Code'], 'Charges Code');
      this.billingCustomerCode = this.customerCodeList.getSelected(details['Billing Customer'], 'Customer Code');
      this.selectedJobType = this.jobTypeList.getSelected(details['Job Type'], 'Job Type');
      this.selectedRateCategory = this.rateCategoryList.getSelected(details['BillRate Group'], 'BillRateGroup');
      await this.getRefList();
      this.selectedReferenceValue = this.referenceList.getSelected(details['Ref Value'], 'referenceValue');
      this.createdBy = `${details['UserCreated'] || ''} ${
        details['DateCreated'] ? new Date(details['DateCreated']).formatString('dd/MM/yyyy HH:mm') : ''
      }`;
      if (details['UserId'] && details['DateUpdated']) {
        this.updatedBy = `${details['UserId'] || ''} ${
          details['DateUpdated'] ? new Date(details['DateUpdated']).formatString('dd/MM/yyyy HH:mm') : ''
        }`;
      } else {
        this.updatedBy = '';
      }
      if (details['LocationType'] === '0') {
        this.selectedSetRate = this.setRateList.find((item) => item.keyValue === 0);
        this.selectedFrom = this.zoneList.getSelected(details['From Zone'], 'Zone Type');
        this.selectedTo = this.zoneList.getSelected(details['To Zone'], 'Zone Type');
      } else if (details['LocationType'] === '1') {
        this.selectedSetRate = this.setRateList.find((item) => item.keyValue === 1);
        this.selectedFrom = this.areaList.getSelected(details['From Area'], 'Area Code');
        this.selectedTo = this.areaList.getSelected(details['To Area'], 'Area Code');
      } else if (details['LocationType'] === '2') {
        this.selectedSetRate = this.setRateList.find((item) => item.keyValue === 2);
        this.selectedFrom = this.destinationList.getSelected(details['From Dest'], 'Destination Code');
        this.selectedTo = this.destinationList.getSelected(details['To Dest'], 'Destination Code');
      }
      this.currency = this.currencyList.getSelected(details['Currency'], 'Currency Code');
      this.rate = details['Rate'];
      this.selectedRateType = this.rateTypeList.find((item) => item.label === details['Rate Type']);
      this.markRate = details['MarkUp Rate'];
      this.selectedMarkUpRate = this.rateTypeList.find((item) => item.label === details['MarkUp Rate Type']);
      this.journeyRate = details['Per Journey Rate'];
      this.truckType = this.truckTypeList.getSelected(details['Truck Type'], 'Truck');
      this.droppointRate = details['Drop Point Rate'];
      this.drop = parseFloat(details['Drops above']).toFixed(0);
      this.validFrom = details['Start Valid Date'] ? new Date(details['Start Valid Date']) : null;
      this.$fullscreenLoading();
    },

    getAllBillRateOthersDetails: async function () {
      this.$fullscreenLoading('Getting Bill Rate Others');
      this.billList = [];
      this.billList = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'BILLRATEOTHERS',
      });
      this.$fullscreenLoading();
    },

    getRefList: async function () {
      if (!this.chargeCode || !this.billingCustomerCode) {
        this.referenceList = [];
        this.showReferenceInput = false;
        return;
      }

      this.$fullscreenLoading('Fetching Reference List');

      const dataToSend = {
        chargescode: this.chargeCode['Charges Code'],
        billcustno: this.billingCustomerCode['Customer Code'],
        refvalue: '',
      };

      const response = await this.$apiCallV2('/BillRateOthers/GetRefList', dataToSend);

      const result = response;
      if (result.Results === '0') {
        if (result.Parameter.FieldHCharges && result.Parameter.TableHCharges) {
          this.fieldHCharges = result.Parameter.FieldHCharges;
          this.tableHCharges = result.Parameter.TableHCharges;
          this.isReferenceValueRequired = result.Parameter.CompulsoryRefValue === '1';
          const dropdownData = result.Parameter.Dropdown;
          if (dropdownData && dropdownData.length > 0) {
            this.referenceList = dropdownData.map((item) => ({ referenceValue: item }));
            this.showReferenceInput = true;
          } else {
            this.referenceList = [];
            this.showReferenceInput = false;
          }
        } else {
          this.referenceList = [];
          this.showReferenceInput = false;
        }
      }
      this.$fullscreenLoading();
    },

    handleMenuClick: async function (menu) {
      this.errorMessage = [];
      this.entryError = [];
      this.successMessages = [];
      this.clearErrorMessages();

      switch (menu.title) {
        case 'View Bill Rate Others':
          if (this.selectedBillRateOthers.length == 0) {
            this.$showAlert('error', 'No Bill Rate Others Selected', 'Please select a record to view');
          } else if (this.selectedBillRateOthers.length > 1) {
            this.$showAlert('error', 'More than 1 record Selected', 'Please select a record to view');
            this.selectedBillRateOthers = [];
          } else {
            await this.getSpecificBillRateOthers(this.selectedBillRateOthers[0]);
            this.disableChargeCode = true;
            this.disablePopupEntryFields();
            this.viewMode = true;
            this.titleBillRateOthers = 'View Bill Rate Others';
            this.showPopup = true;
          }
          break;
        case 'Edit Bill Rate Others':
          if (this.selectedBillRateOthers.length == 0) {
            this.$showAlert('error', 'No Bill Rate Others Selected', 'Please select a record to edit');
          } else if (this.selectedBillRateOthers.length > 1) {
            this.$showAlert('error', 'More than 1 record Selected', 'Please select a record to edit');
            this.selectedBillRateOthers = [];
          } else {
            await this.getSpecificBillRateOthers(this.selectedBillRateOthers[0]);

            this.saveOption = 'E';
            this.disableChargeCode = true;
            this.viewMode = false;
            this.enablePopupEntryFields();
            this.titleBillRateOthers = 'Edit Bill Rate Others';
            this.showPopup = true;
            this.selectedBillRateOthers = [];
            this.$nextTick(() => {
              this.$refs['billingCustomerCode'].setFocus();
            });
          }
          break;
        case 'New Bill Rate Others':
          this.clearInputFields();
          this.saveOption = 'N';
          this.initialData();
          this.disableChargeCode = false;
          this.viewMode = false;
          this.enablePopupEntryFields();
          this.validFrom = new Date();
          this.currency = this.currencyList.getSelected('MYR', 'Currency Code');
          this.titleBillRateOthers = 'New Bill Rate Others';
          this.showPopup = true;
          this.$nextTick(() => {
            this.$refs['chargeCode'].setFocus();
          });
          this.selectedBillRateOthers = [];
          break;
        case 'Delete Bill Rate Others':
          if (!this.selectedBillRateOthers.length) {
            this.$showAlert('error', 'No Bill Rate Others Selected', 'Please select record(s) to delete');
            return;
          }

          {
            const deleting = await this.$showAlert(
              'warning',
              'Delete Bill Rate Others',
              `Are you sure you want to delete ${this.selectedBillRateOthers.length} record(s)?`,
              [
                { text: 'Delete', textColor: 'white', color: 'red' },
                { text: 'Cancel', textColor: 'black', color: 'lightgray' },
              ],
            );

            if (deleting == 'Cancel') {
              this.selectedBillRateOthers = [];
              return;
            }

            this.$fullscreenLoading('Deleting Bill Rate Others');
            const recordsToDelete = this.selectedBillRateOthers.map((record) => ({
              chargescode: record['Charges Code'],
              seqno: record['Seq'],
            }));
            const dataToPost = {
              action: 'D',
              record: recordsToDelete,
            };
            const result = await this.$apiCallV2('/BillRateOthers/SaveBillRateOthers', dataToPost);
            this.$fullscreenLoading();
            if (result[0]['Results'] == 0) {
              this.$showAlert(
                'success',
                'Bill Rate Others Deleted',
                `Successfully deleted ${this.selectedBillRateOthers.length} record(s).`,
              );
              await this.getAllBillRateOthersDetails();
              this.selectedBillRateOthers = [];
            } else {
              this.$showAlert(
                'error',
                'Bill Rate Others NOT Deleted',
                result[0]['ErrMsg'] || 'There was an error deleting the selected record(s).',
              );
              this.selectedBillRateOthers = [];
            }
          }
          break;
        case 'Duplicate':
          if (this.selectedBillRateOthers.length == 0) {
            this.$showAlert('error', 'No Bill Rate Others Selected', 'Please select a record to duplicate');
          } else if (this.selectedBillRateOthers.length > 1) {
            this.$showAlert('error', 'More than 1 record Selected', 'Please select a single record to duplicate');
            this.selectedBillRateOthers = [];
          } else {
            this.viewMode = false;
            await this.getSpecificBillRateOthers(this.selectedBillRateOthers[0]);
            this.saveOption = 'N';
            this.seqno = '';
            this.createdBy = '';
            this.updatedBy = '';
            this.titleBillRateOthers = 'Duplicate Bill Rate Others';
            this.enablePopupEntryFields();
            this.disableChargeCode = false;
            this.showPopup = true;
            this.selectedBillRateOthers = [];
          }
          break;
      }
    },

    onRateTypeChanged: function () {
      this.selectedFrom = null;
      this.selectedTo = null;
    },

    enablePopupEntryFields: function () {
      this.disableBillingCustomerCode = false;
      this.disableCurrency = false;
      this.disableFrom = false;
      this.disableTo = false;
      this.disableRate = false;
      this.disableRateType = false;
      this.disableMarkUpRate = false;
      this.disableMarkUpRateType = false;
      this.disableJourneyRate = false;
      this.disableDroppointRate = false;
      this.disableDrop = false;
      this.disableSetRate = false;
      this.disableJobType = false;
      this.disableRateCategory = false;
      this.disableDescription = false;
      this.disableReferenceValue = false;
      this.disableDate = false;
    },

    disablePopupEntryFields: function () {
      this.disableBillingCustomerCode = true;
      this.disableCurrency = true;
      this.disableFrom = true;
      this.disableTo = true;
      this.disableRate = true;
      this.disableRateType = true;
      this.disableMarkUpRate = true;
      this.disableMarkUpRateType = true;
      this.disableJourneyRate = true;
      this.disableDroppointRate = true;
      this.disableDrop = true;
      this.disableSetRate = true;
      this.disableJobType = true;
      this.disableRateCategory = true;
      this.disableDescription = true;
      this.disableReferenceValue = true;
      this.disableDate = true;
    },

    saveBillRateOthers: async function () {
      this.entryError = [];
      this.errorMessage = [];
      const isValid = this.checkFields();
      if (isValid) {
        const dataToPost = {
          action: this.saveOption,
          seqno: this.seqno || '',
          chargescode: this.chargeCode['Charges Code'],
          billingcustno: this.billingCustomerCode ? this.billingCustomerCode['Customer Code'] : '',
          jobtype: this.selectedJobType ? this.selectedJobType['Job Type'] : '',
          billrategroup: this.selectedRateCategory ? this.selectedRateCategory['BillRateGroup'] : '',
          refvalue: this.selectedReferenceValue ? this.selectedReferenceValue['referenceValue'] : '',
          tablehcharges: this.tableHCharges,
          fieldhcharges: this.fieldHCharges,
          fromarea: this.selectedSetRate.keyValue === 1 && this.selectedFrom ? this.selectedFrom['Area Code'] : '',
          toarea: this.selectedSetRate.keyValue === 1 && this.selectedTo ? this.selectedTo['Area Code'] : '',
          fromzone: this.selectedSetRate.keyValue === 0 && this.selectedFrom ? this.selectedFrom['Zone Type'] : '',
          tozone: this.selectedSetRate.keyValue === 0 && this.selectedTo ? this.selectedTo['Zone Type'] : '',
          fromdest:
            this.selectedSetRate.keyValue === 2 && this.selectedFrom ? this.selectedFrom['Destination Code'] : '',
          todest: this.selectedSetRate.keyValue === 2 && this.selectedTo ? this.selectedTo['Destination Code'] : '',
          currency: this.currency ? this.currency['Currency Code'] : '',
          rate: this.rate,
          ratetype: this.selectedRateType ? this.selectedRateType['label'] : '',
          markuprate: this.markRate,
          markupratetype: this.selectedMarkUpRate ? this.selectedMarkUpRate['label'] : '',
          perjourneyrate: this.journeyRate,
          trucktype: this.truckType ? this.truckType['Truck'] : '',
          droppointrate: this.droppointRate,
          droppointthreshold: this.drop,
          startvaliddate: this.validFrom,
        };

        if (this.saveOption === 'N') {
          dataToPost.seqno = '';
        }
        this.$fullscreenLoading('Saving Bill Rate Others');
        const postUpdate = await this.$apiCallV2('/BillRateOthers/SaveBillRateOthers', dataToPost);
        this.$fullscreenLoading();
        if (postUpdate[0].Results == 1 || postUpdate[0].Results == 2) {
          this.errorMessage.push(`There was an error: ${postUpdate[0].ErrMsg}`);
        } else {
          this.seqno = postUpdate[0].seqno || this.seqno;
          if (this.saveOption == 'N') {
            this.successMessages.push('Successfully created Bill Rate Others : ' + this.chargeCode['Charges Code']);
          }
          if (this.saveOption == 'E') {
            this.successMessages.push('Successfully edited Bill Rate Others : ' + this.chargeCode['Charges Code']);
          }
          this.viewMode = true;
          this.disableChargeCode = true;
          this.titleBillRateOthers = 'View Bill Rate Others';
          this.getAllBillRateOthersDetails();
          this.getSpecificBillRateOthers({
            Seq: this.seqno,
            'Charges Code': this.chargeCode['Charges Code'],
          });
          this.disablePopupEntryFields();
        }
      }
    },

    initialData: function () {
      this.rate = '0.00';
      this.markRate = '0.00';
      this.journeyRate = '0.00';
      this.droppointRate = '0.00';
      this.drop = '0';
      this.selectedRateType = this.rateTypeList[0];
      this.selectedMarkUpRate = this.rateTypeList[0];
      this.selectedSetRate = this.setRateList[0];
    },

    checkFields: function () {
      this.clearErrorMessages();
      let isValid = true;

      if (!this.chargeCode) {
        this.errorChargeCode = 'Charge Code is required!';
        this.entryError.push({ key: 'chargeCode', value: this.errorChargeCode });
        isValid = false;
      }
      if (!this.selectedRateCategory) {
        this.errorRateCategory = 'Rate Category is required!';
        this.entryError.push({ key: 'rateCategory', value: this.errorRateCategory });
        isValid = false;
      }
      if (!this.currency) {
        this.errorCurrencyCode = 'Currency is required!';
        this.entryError.push({ key: 'currency', value: this.errorCurrencyCode });
        isValid = false;
      }
      if (!this.selectedMarkUpRate) {
        this.errorMarkUpRateType = 'Mark-Up Rate Type is required!';
        this.entryError.push({ key: 'markUpRateType', value: this.errorMarkUpRateType });
        isValid = false;
      }
      if (!this.selectedRateType) {
        this.errorRateType = 'Rate Type is required!';
        this.entryError.push({ key: 'rateType', value: this.errorRateType });
        isValid = false;
      }
      if (!this.rate || !this.rate.trim() === '') {
        this.errorRate = 'Rate is required!';
        this.entryError.push({ key: 'rate', value: this.errorRate });
        isValid = false;
      }
      if (!this.markRate || !this.markRate.trim() === '') {
        this.errorMarkRate = 'Mark-Up Rate is required!';
        this.entryError.push({ key: 'markRate', value: this.errorMarkRate });
        isValid = false;
      }
      if (!this.selectedReferenceValue && this.showReferenceInput && this.isReferenceValueRequired) {
        this.errorReference = 'Reference Value is required!';
        this.entryError.push({ key: 'Reference Value', value: this.errorReference });
        isValid = false;
      }
      if (!this.validFrom) {
        this.errorDate = 'Valid From is required!';
        this.entryError.push({ key: 'Date', value: this.errorDate });
        isValid = false;
      } else {
        const maxDate = new Date('2079-06-06');
        if (new Date(this.validFrom) > maxDate) {
          this.errorDate = 'Valid From date cannot be later than 2079-06-06';
          this.entryError.push({ key: 'Date', value: this.errorDate });
          isValid = false;
        }
        if (new Date(this.validFrom) < new Date('1970-01-01')) {
          this.errorDate = 'Valid From date cannot be earlier than 1970-01-01';
          this.entryError.push({ key: 'Date', value: this.errorDate });
          isValid = false;
        }
      }
      return isValid;
    },
  },
  async mounted() {
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'Bill Rate Others') {
        x.data.forEach((k) => {
          const selectedMenu = this.menus.find((m) => m['key'] == k['Control']);
          if (selectedMenu) {
            selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] == 'True';
          }
        });
      }
    });
    const promises = [];
    let promisesFulfilled = 0;
    promises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CHARGESCODE', module: 'BILLRATEOT' }).then((result) => {
        this.chargeCodeList = result.map((item) => ({
          ...item,
          combinedChargeCodeName: `${item['Charges Code']} - ${item['Description']}`,
        }));
        this.$topLoading(++promisesFulfilled / promises.length);
      }),
    );
    promises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'BILLCUSTNAME' }).then((result) => {
        this.customerCodeList = result.map((item) => ({
          ...item,
          combinedCustomerCode: `${item['Customer Code']} - ${item['Customer Name']}`,
        }));
        this.$topLoading(++promisesFulfilled / promises.length);
      }),
    );
    promises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'JOBTYPE', module: 'TMS' }).then((result) => {
        this.jobTypeList = result;
        this.$topLoading(++promisesFulfilled / promises.length);
      }),
    );
    promises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'BILLRATEGROUP' }).then((result) => {
        this.rateCategoryList = result;
        this.$topLoading(++promisesFulfilled / promises.length);
      }),
    );
    promises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'ZONE' }).then((result) => {
        this.zoneList = result.map((item) => ({
          ...item,
          combinedZone: `${item['Zone Type']} - ${item['Description']}`,
        }));
        this.$topLoading(++promisesFulfilled / promises.length);
      }),
    );
    promises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'AREA' }).then((result) => {
        this.areaList = result.map((item) => ({
          ...item,
          combinedArea: `${item['Area Code']} - ${item['Description']}`,
        }));
        this.$topLoading(++promisesFulfilled / promises.length);
      }),
    );
    promises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'DESTINATION' }).then((result) => {
        this.destinationList = result.map((item) => ({
          ...item,
          combinedDestinationCode: `${item['Destination Code']} - ${item['Company']}`,
        }));
        this.$topLoading(++promisesFulfilled / promises.length);
      }),
    );
    promises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CURRENCY' }).then((result) => {
        this.currencyList = result.map((item) => ({
          ...item,
          combinedCurrency: `${item['Currency Code']} - ${item['Description']}`,
        }));
        this.$topLoading(++promisesFulfilled / promises.length);
      }),
    );
    promises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'TRUCKTYPE', module: 'BILLRATEOT' }).then((result) => {
        this.truckTypeList = result.map((item) => ({
          ...item,
          combinedTruckType: `${item['Truck']} - ${item['Truck Description']}`,
        }));
        this.$topLoading(++promisesFulfilled / promises.length);
      }),
    );
    this.getAllBillRateOthersDetails();
    Promise.allSettled(promises).then((_promiseOutcomes) => {
      this.menus = this.menus.map((menu) => ({
        ...menu,
        enabled: true,
      }));
    });
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.billRateOthers-main {
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  overflow: hidden;
  font-size: 12px;
  display: flex;

  > .right-container {
    background-color: white;
    padding: 5px;
    border-radius: 5px;
    width: 100%;
    margin-left: 5px;
    overflow: hidden;
  }
}

.flex-row {
  display: flex;
  align-items: center;

  > * {
    width: 100%;
  }

  > :not(:first-child) {
    margin-left: 5px;
  }
}
</style>
